import React from 'react';

type ThemeMode = 'light' | 'dark';

type ThemeColors = {
  bg: string;
  surface: string;
  surfaceMuted: string;
  text: string;
  textMuted: string;
  border: string;
  primary: string;
  primaryHover: string;
  onPrimary: string;
  danger: string;
  success: string;
};

type ThemeFonts = {
  body: string;
  heading: string;
  mono: string;
};

export type PreferredColorScheme = 'light' | 'dark' | null;

export type ThemeVariables = {
  fonts: ThemeFonts;
  light: ThemeColors;
  dark: ThemeColors;
  preferredColorScheme?: PreferredColorScheme;
};

type ThemeVariablesUpdate = {
  fonts?: Partial<ThemeFonts>;
  light?: Partial<ThemeColors>;
  dark?: Partial<ThemeColors>;
  preferredColorScheme?: PreferredColorScheme;
};

type ThemeVariablesContextValue = {
  theme: ThemeVariables;
  setTheme: React.Dispatch<React.SetStateAction<ThemeVariables>>;
  updateTheme: (update: ThemeVariablesUpdate) => void;
  resetTheme: () => void;
};

declare global {
  interface Window {
    __safBrandingTheme?: ThemeVariables;
  }
}

const DEFAULT_THEME: ThemeVariables = {
  fonts: {
    body: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    heading: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'Fira Code', 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  light: {
    bg: '#ffffff',
    surface: '#ffffff',
    surfaceMuted: '#f3f4f6',
    text: '#0f172a',
    textMuted: '#64748b',
    border: '#e5e7eb',
    primary: '#228be6',
    primaryHover: '#1864ab',
    onPrimary: '#ffffff',
    danger: '#b91c1c',
    success: '#16a34a',
  },
  dark: {
    bg: '#0b1220',
    surface: '#0f172a',
    surfaceMuted: '#111827',
    text: '#f8fafc',
    textMuted: '#cbd5e1',
    border: '#334155',
    primary: '#60a5fa',
    primaryHover: '#93c5fd',
    onPrimary: '#0b1220',
    danger: '#f87171',
    success: '#4ade80',
  },
};

const ThemeVariablesContext = React.createContext<ThemeVariablesContextValue | undefined>(undefined);

function setDocumentColorScheme(mode: ThemeMode) {
  if (typeof document === 'undefined') {
    return;
  }

  const targets = [document.documentElement, document.body, document.getElementById('root')].filter(
    Boolean,
  ) as HTMLElement[];

  targets.forEach((target) => {
    if (target.getAttribute('data-mantine-color-scheme') !== mode) {
      target.setAttribute('data-mantine-color-scheme', mode);
    }
  });
}

function getInitialTheme(): ThemeVariables {
  if (typeof window !== 'undefined' && window.__safBrandingTheme) {
    return window.__safBrandingTheme;
  }
  return DEFAULT_THEME;
}

function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  const trimmed = (hex ?? '').trim();
  if (!trimmed.startsWith('#')) {
    return null;
  }

  const raw = trimmed.slice(1);
  const isShort = raw.length === 3;
  const isLong = raw.length === 6;
  if (!isShort && !isLong) {
    return null;
  }

  const expanded = isShort
    ? raw
        .split('')
        .map((c) => `${c}${c}`)
        .join('')
    : raw;

  const r = Number.parseInt(expanded.slice(0, 2), 16);
  const g = Number.parseInt(expanded.slice(2, 4), 16);
  const b = Number.parseInt(expanded.slice(4, 6), 16);

  if ([r, g, b].some((v) => Number.isNaN(v))) {
    return null;
  }

  return { r, g, b };
}

function toRgbVarString(hex: string): string {
  const parsed = parseHexColor(hex);
  if (!parsed) {
    return '34, 139, 230';
  }
  return `${parsed.r}, ${parsed.g}, ${parsed.b}`;
}

function getDocumentThemeMode(): ThemeMode {
  if (typeof document === 'undefined') {
    return 'light';
  }

  const scheme =
    document.documentElement.getAttribute('data-mantine-color-scheme') ??
    document.body?.getAttribute('data-mantine-color-scheme') ??
    document.getElementById('root')?.getAttribute('data-mantine-color-scheme');

  if (scheme === 'dark') {
    return 'dark';
  }

  if (scheme === 'light') {
    return 'light';
  }

  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  return 'light';
}

function applyActiveModeToDocument(theme: ThemeVariables): void {
  if (typeof document === 'undefined') {
    return;
  }

  const mode = getDocumentThemeMode();
  setDocumentColorScheme(mode);
  const colors = mode === 'dark' ? theme.dark : theme.light;

  const primaryRgb = toRgbVarString(colors.primary);
  const dangerRgb = toRgbVarString(colors.danger);
  const successRgb = toRgbVarString(colors.success);

  const targets: HTMLElement[] = [document.documentElement];
  if (document.body) {
    targets.push(document.body);
  }
  const appRoot = document.getElementById('root');
  if (appRoot) {
    targets.push(appRoot);
  }

  targets.forEach((target) => {
    target.style.setProperty('--saf-color-bg', colors.bg);
    target.style.setProperty('--saf-color-surface', colors.surface);
    target.style.setProperty('--saf-color-surface-muted', colors.surfaceMuted);
    target.style.setProperty('--saf-color-text', colors.text);
    target.style.setProperty('--saf-color-text-muted', colors.textMuted);
    target.style.setProperty('--saf-color-border', colors.border);
    target.style.setProperty('--saf-color-primary', colors.primary);
    target.style.setProperty('--saf-color-primary-hover', colors.primaryHover);
    target.style.setProperty('--saf-color-on-primary', colors.onPrimary);
    target.style.setProperty('--saf-color-danger', colors.danger);
    target.style.setProperty('--saf-color-success', colors.success);
    target.style.setProperty('--saf-color-primary-rgb', primaryRgb);
    target.style.setProperty('--saf-color-danger-rgb', dangerRgb);
    target.style.setProperty('--saf-color-success-rgb', successRgb);

    target.style.setProperty('--mantine-color-default-border', colors.border);
    target.style.setProperty('--mantine-color-body', colors.bg);
    target.style.setProperty('--mantine-color-default', colors.surface);
    target.style.setProperty('--mantine-color-text', colors.text);
    target.style.setProperty('--mantine-color-dimmed', colors.textMuted);

    target.style.setProperty('--mantine-color-blue-6', colors.primary);
    target.style.setProperty('--mantine-color-blue-7', colors.primaryHover);
    target.style.setProperty('--mantine-color-blue-8', colors.primary);
    target.style.setProperty('--mantine-color-blue-9', colors.primaryHover);
    target.style.setProperty('--mantine-color-blue-5', colors.primary);
    target.style.setProperty('--mantine-color-blue-4', colors.primary);
    target.style.setProperty('--mantine-color-blue-3', `rgba(${primaryRgb}, 0.35)`);
    target.style.setProperty('--mantine-color-blue-1', `rgba(${primaryRgb}, 0.12)`);
    target.style.setProperty('--mantine-color-blue-0', `rgba(${primaryRgb}, 0.08)`);
    target.style.setProperty('--mantine-color-blue-light', `rgba(${primaryRgb}, 0.12)`);
  });
}

export function applyThemeToDocument(theme: ThemeVariables): void {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;

  root.style.setProperty('--saf-font-body', theme.fonts.body);
  root.style.setProperty('--saf-font-heading', theme.fonts.heading);
  root.style.setProperty('--saf-font-mono', theme.fonts.mono);

  const applyMode = (mode: ThemeMode, colors: ThemeColors) => {
    root.style.setProperty(`--saf-color-bg-${mode}`, colors.bg);
    root.style.setProperty(`--saf-color-surface-${mode}`, colors.surface);
    root.style.setProperty(`--saf-color-surface-muted-${mode}`, colors.surfaceMuted);
    root.style.setProperty(`--saf-color-text-${mode}`, colors.text);
    root.style.setProperty(`--saf-color-text-muted-${mode}`, colors.textMuted);
    root.style.setProperty(`--saf-color-border-${mode}`, colors.border);
    root.style.setProperty(`--saf-color-primary-${mode}`, colors.primary);
    root.style.setProperty(`--saf-color-primary-hover-${mode}`, colors.primaryHover);
    root.style.setProperty(`--saf-color-on-primary-${mode}`, colors.onPrimary);
    root.style.setProperty(`--saf-color-danger-${mode}`, colors.danger);
    root.style.setProperty(`--saf-color-success-${mode}`, colors.success);
    root.style.setProperty(`--saf-color-primary-rgb-${mode}`, toRgbVarString(colors.primary));
    root.style.setProperty(`--saf-color-danger-rgb-${mode}`, toRgbVarString(colors.danger));
    root.style.setProperty(`--saf-color-success-rgb-${mode}`, toRgbVarString(colors.success));
  };

  applyMode('light', theme.light);
  applyMode('dark', theme.dark);

  applyActiveModeToDocument(theme);
}

export function ThemeVariablesProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = React.useState<ThemeVariables>(() => getInitialTheme());

  const themeRef = React.useRef(theme);

  React.useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  React.useEffect(() => {
    applyThemeToDocument(theme);
    if (typeof window !== 'undefined') {
      window.__safBrandingTheme = theme;
    }
  }, [theme]);

  // Listen for earlyBranding.ts theme load event
  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Check if earlyBranding already loaded the theme before we mounted
    if (window.__safBrandingTheme && window.__safBrandingTheme !== theme) {
      setTheme(window.__safBrandingTheme);
    }

    const handleBrandingThemeLoaded = (event: Event) => {
      const customEvent = event as CustomEvent<ThemeVariables>;
      if (customEvent.detail) {
        setTheme(customEvent.detail);
      }
    };

    window.addEventListener('saf-branding-theme-loaded', handleBrandingThemeLoaded);
    return () => {
      window.removeEventListener('saf-branding-theme-loaded', handleBrandingThemeLoaded);
    };
  }, []);

  React.useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const observer = new MutationObserver(() => {
      applyActiveModeToDocument(themeRef.current);
    });

    const targets: Node[] = [document.documentElement];
    if (document.body) {
      targets.push(document.body);
    }
    const appRoot = document.getElementById('root');
    if (appRoot) {
      targets.push(appRoot);
    }

    targets.forEach((target) => {
      observer.observe(target, {
        attributes: true,
        attributeFilter: ['data-mantine-color-scheme'],
      });
    });

    applyActiveModeToDocument(themeRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const updateTheme = React.useCallback((update: ThemeVariablesUpdate) => {
    setTheme((current) => ({
      fonts: update.fonts ? { ...current.fonts, ...update.fonts } : current.fonts,
      light: update.light ? { ...current.light, ...update.light } : current.light,
      dark: update.dark ? { ...current.dark, ...update.dark } : current.dark,
      preferredColorScheme: update.preferredColorScheme !== undefined ? update.preferredColorScheme : current.preferredColorScheme,
    }));
  }, []);

  const resetTheme = React.useCallback(() => {
    setTheme(DEFAULT_THEME);
  }, []);

  const value = React.useMemo<ThemeVariablesContextValue>(
    () => ({
      theme,
      setTheme,
      updateTheme,
      resetTheme,
    }),
    [resetTheme, theme, updateTheme]
  );

  return <ThemeVariablesContext.Provider value={value}>{children}</ThemeVariablesContext.Provider>;
}

export function useThemeVariables(): ThemeVariablesContextValue {
  const context = React.useContext(ThemeVariablesContext);
  if (!context) {
    throw new Error('useThemeVariables must be used within ThemeVariablesProvider');
  }
  return context;
}

export { DEFAULT_THEME };
